import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import {
    Grid,
    FormControl,
    Select,
    Chip,
    InputLabel,
    SelectChangeEvent,
    FormHelperText,
    MenuItem,
    Checkbox,
    ListItemText,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { AnyMultiselectProps } from './AnyMultiselect.types'
import { AnyComponentRef } from './AnyComponent.types'
import { TEXT_REQUIRED_VALIDATOR } from '../validators'

const MultiselectContainer = styled(Grid)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
}))

const MultiselectChip = styled(Chip)(({ theme }) => ({
    margin: 2,
}))

function AnyMultiselect({
    variant = 'outlined',
    size = 'small',
    margin = 'dense',
    fullWidth = true,
    name,
    initialValue,
    label,
    options,
    required,
    disabled,
    fixed,
    color = 'primary',
    register,
    notifier,
}: AnyMultiselectProps) {
    const [value, setValue] = useState<string[]>(() => {
        const defaultValues = initialValue ?? []
        if (!fixed) {
            return defaultValues
        }

        const missing = defaultValues.filter((el) => fixed.indexOf(el) >= 0)
        return [...fixed, ...missing]
    })
    const [blured, setBlured] = useState(false)
    const [valid, setValid] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [error, setError] = useState<string>()

    const ref = useRef<AnyComponentRef<string[]>>({
        valid,
        dirty,
        value,
        setBlur: () => setBlured(true),
    })

    const handleChange = useCallback(
        (event: SelectChangeEvent<string[]>, child: React.ReactNode) => {
            const value = (event.target.value as string[]) || []
            setValue(value)
            setDirty(true)
        },
        []
    )

    const onBlur = useCallback(
        (event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setBlured(dirty)
        },
        [dirty]
    )

    const onClose = useCallback(() => {
        setBlured(dirty) // solo se marca como blured si ya fue modificado algún valor
    }, [dirty])

    const onNotify = useCallback(
        (value?: string[]) => {
            if (notifier) {
                notifier(name, value)
            }
        },
        [notifier, name]
    )

    useEffect(() => {
        const valid = !required || value.length > 0
        const error = valid ? undefined : TEXT_REQUIRED_VALIDATOR.getMessage()

        ref.current.valid = valid
        ref.current.dirty = dirty
        ref.current.value = value

        setValid(valid)
        setError(error)

        onNotify(value)
    }, [value, dirty, required, onNotify])

    useEffect(() => {
        register(name, ref.current)
    }, [register, name, ref])

    useEffect(() => {
        const allowed = options.map((el) => el.value)
        const initial = initialValue ?? []
        if (disabled && initial.length === 0) {
            setValue([])
        } else {
            const value = initial.filter((el) => allowed.indexOf(el) >= 0)
            if (fixed) {
                const missing = fixed.filter((el) => value.indexOf(el) < 0)
                setValue([...value, ...missing])
            } else {
                setValue(value)
            }
        }
    }, [initialValue, options, fixed, disabled])

    return (
        <FormControl
            variant={variant}
            margin={margin}
            size={size}
            required={required}
            fullWidth={fullWidth}
            error={blured && !valid}
        >
            <InputLabel htmlFor={`multiselect-chip-${name}`}>
                {label}
            </InputLabel>
            <Select
                label={label}
                value={value}
                multiple
                onChange={handleChange}
                onClose={onClose}
                onBlur={onBlur}
                required={required}
                disabled={disabled}
                inputProps={{
                    id: `multiselect-chip-${name}`,
                    name: name,
                }}
                renderValue={(selected) => {
                    return (
                        <MultiselectContainer>
                            {(selected as string[]).map((value) => {
                                const el = options.find(
                                    (el) => el.value === value
                                )
                                return (
                                    <MultiselectChip
                                        key={value}
                                        label={el?.label || value}
                                        color={disabled ? 'default' : color}
                                    />
                                )
                            })}
                        </MultiselectContainer>
                    )
                }}
            >
                {options.map((el) => {
                    const selected = value?.indexOf(el.value) >= 0
                    const disabled = !!fixed && fixed.indexOf(el.value) >= 0

                    return (
                        <MenuItem
                            key={el.value}
                            value={el.value}
                            selected={selected}
                            disabled={disabled}
                            dense
                            sx={{ py: 0 }}
                        >
                            <Checkbox
                                size="small"
                                color={color}
                                checked={selected}
                            />
                            <ListItemText primary={el.label} />
                        </MenuItem>
                    )
                })}
            </Select>
            {blured && !valid && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
    )
}

export default memo(AnyMultiselect)
